import { Item } from '@react-stately/collections'
import { CollectionChildren } from '@react-types/shared'
import { AI as BlazeAI, MessageText2 } from 'blaze-ui/icons'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { type EditorView } from 'prosemirror-view'
import { useArticleView } from 'providers/ArticleViewProvider/ArticleViewProvider'
import { useLayoutEffect } from 'react'
import { useDocStore } from 'stores/DocStore'
import { ArticleFlat, ArticleView, DocRightRailViewItem } from 'types'
import { AI_VIEW, COMMENT_VIEW, METADATA_VIEW } from '../DocRightRail/Constants'
import { Collaboration } from './Collaboration/Collaboration'
import Styles from './DocRightRailV2.module.scss'
import { Tabs } from './Tabs/Tabs'
import { useCollaborationCount } from './UseCollaborationCount'
import { useAvailableTabs } from './useAvailableTabs'
import { useTabSelection } from './useTabSelection'
import { AIAssistant } from 'components/DocRightRail/screens/AIAssistant/AIAssistant'
import { Counter, CounterSize, CounterVariant } from 'blaze-ui/Counter/Counter'
import { BlogMetadataContainer } from 'components/BlogMetadata/PollingBlogMetadata'

const AIIcon = () => <BlazeAI color="var(--dark-100)" />

export const DocRightRailV2 = observer(function DocRightRailV2({
  view,
  article,
}: {
  view?: EditorView
  article: ArticleFlat
}) {
  const availableTabs = useAvailableTabs({ article })
  const docStore = useDocStore()
  const collapsed = !docStore.rightRailStore.locked
  const { selectedTab } = useTabSelection(docStore)
  const articleView = useArticleView()
  const collaborationCount = useCollaborationCount()

  const handleTabClick = (tab: React.Key) => {
    docStore.rightRailStore.setActiveTab(tab as DocRightRailViewItem)
  }

  useLayoutEffect(() => {
    document.querySelector('html')?.classList.add('jan-release')
  }, [])

  const collaborationTab = availableTabs[COMMENT_VIEW] && (
    <Item
      key={COMMENT_VIEW}
      title={
        <>
          <MessageText2 />
          {collaborationCount > 0 && (
            <div
              className={cx(Styles.tabCounter, {
                [Styles.collapsed]: collapsed,
              })}
            >
              <Counter count={collaborationCount} size={CounterSize.xs} variant={CounterVariant.Dark} />
            </div>
          )}
        </>
      }
      aria-label={articleView === ArticleView.Commenting ? 'Comments' : 'Collaboration'}
    >
      <Collaboration article={article} onClose={() => handleTabClick(AI_VIEW)} />
    </Item>
  )

  const aiAssistantTab = availableTabs[AI_VIEW] && docStore.layerId && (
    <Item key={AI_VIEW} title={<AIIcon />} aria-label="AI Assistant">
      <AIAssistant article={article} layerId={docStore.layerId} className={Styles.stackableContainer} />
    </Item>
  )

  const metadataTab = availableTabs[METADATA_VIEW] && (
    <Item key="Metadata" aria-label="Metadata">
      <BlogMetadataContainer articleId={article.id} />
    </Item>
  )

  const tabs = [aiAssistantTab, metadataTab, collaborationTab]

  return (
    <>
      <Tabs selectedKey={selectedTab} onSelectionChange={handleTabClick}>
        {tabs.filter(Boolean) as CollectionChildren<HTMLElement>}
      </Tabs>
    </>
  )
})
